/**
 * Imprime la comanda en impresora térmica.
 * Android  → rawbt: URI scheme con ESC/POS directo a RawBT (sin diálogos).
 * Otros    → ventana popup HTML con window.print() (fallback de desarrollo).
 */
/* eslint-disable no-useless-concat */
import { shouldUseRawBT } from "./printDriver";

// ─── ESC/POS constants ────────────────────────────────────────────────
const ESC = '\x1b';
const GS  = '\x1d';

const EP = {
  INIT:     ESC + '@',
  CP858:    ESC + '\x74\x13',   // Codepage 19 = CP858 (español con tildes y ñ)
  CENTER:   ESC + '\x61\x01',
  LEFT:     ESC + '\x61\x00',
  BOLD_ON:  ESC + '\x45\x01',
  BOLD_OFF: ESC + '\x45\x00',
  DBL:      GS  + '\x21\x11',  // Doble ancho + doble alto
  DBL_H:    GS  + '\x21\x01',  // Solo doble alto (mismo ancho)
  NORMAL:   GS  + '\x21\x00',  // Tamaño normal
  FEED1:    ESC + '\x64\x01',  // Avanzar 1 línea
  FEED3:    ESC + '\x64\x03',  // Avanzar 3 líneas
  CUT:      ESC + '\x69',      // Corte completo (fullCut3)
};

// Tabla CP858: mapea chars Unicode a bytes CP858 para español
const CP858_TABLE = {
  'Ç':'\x80','ü':'\x81','é':'\x82','â':'\x83','ä':'\x84','à':'\x85',
  'å':'\x86','ç':'\x87','ê':'\x88','ë':'\x89','è':'\x8A','ï':'\x8B',
  'î':'\x8C','ì':'\x8D','Ä':'\x8E','Å':'\x8F','É':'\x90','æ':'\x91',
  'Æ':'\x92','ô':'\x93','ö':'\x94','ò':'\x95','û':'\x96','ù':'\x97',
  'ÿ':'\x98','Ö':'\x99','Ü':'\x9A','ø':'\x9B','£':'\x9C','Ø':'\x9D',
  'á':'\xA0','í':'\xA1','ó':'\xA2','ú':'\xA3','ñ':'\xA4','Ñ':'\xA5',
  'ª':'\xA6','º':'\xA7','¿':'\xA8','¡':'\xAD','Á':'\xB5','À':'\xB7',
  'Í':'\xD6','Ó':'\xE0','Ú':'\xE9',
};

/** Convierte texto Unicode a bytes CP858 para la impresora. */
function cp858(str) {
  return [...(str || '')].map(c => CP858_TABLE[c] ?? c).join('');
}

function thermalSafeText(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[‐‑‒–—−]/g, '-')
    .replace(/\u00a0/g, ' ');
}

/**
 * Percent-encode como bytes individuales (NO codificación UTF-8 multi-byte).
 * Necesario para que RawBT reciba los bytes ESC/POS exactos.
 */
function rawbtEncode(str) {
  const SAFE = /[A-Za-z0-9\-_.~]/;
  return [...str].map(c => {
    if (SAFE.test(c)) return c;
    const b = c.charCodeAt(0);
    return '%' + (b < 256 ? b : 63).toString(16).toUpperCase().padStart(2, '0');
  }).join('');
}

function fmtRaw(n) {
  return '$' + (Number(n) || 0).toLocaleString('es-CL');
}

function normalizeModificaciones(item = {}) {
  const raw = item.cambios ?? item.modificaciones ?? item.modificacion ?? item.observaciones ?? item.observacion_item ?? [];
  const list = Array.isArray(raw)
    ? raw
    : String(raw || '').split(/\r?\n/);
  return list.map((x) => String(x || '').trim()).filter(Boolean);
}

function wrapText(text, width) {
  const chunks = [];
  let resto = String(text || '').trim();
  while (resto.length > width) {
    let corte = resto.lastIndexOf(' ', width);
    if (corte <= 0) corte = width;
    chunks.push(resto.substring(0, corte));
    resto = resto.substring(corte).trimStart();
  }
  if (resto) chunks.push(resto);
  return chunks.length ? chunks : [''];
}

/** Fila con texto izquierda y valor alineado a la derecha. */
function rowLR(left, right, width = 48) {
  return left + ' '.repeat(Math.max(1, width - left.length - right.length)) + right;
}

/** Construye las líneas de un item con wrap si el nombre supera 28 chars. */
function buildItemLines(item) {
  const nombre = item?.nombre;
  const cantidad = item?.cantidad;
  const precio = item?.precio;
  const cnt = ('x' + (Number(cantidad) || 0)).padEnd(4);
  const tot = fmtRaw(Number(precio) * Number(cantidad)).padStart(15);

  const nom = String(nombre || ' ');

  // Ahora tenemos más espacio para el nombre
  const maxNombre = 29;

  const chunks = [];
  let resto = nom;

  while (resto.length > maxNombre) {
    let corte = resto.lastIndexOf(' ', maxNombre);

    if (corte <= 0) corte = maxNombre;

    chunks.push(resto.substring(0, corte));
    resto = resto.substring(corte).trimStart();
  }

  chunks.push(resto || ' ');

  let lines = '';

  // Primera línea incluye cantidad
  lines += cnt + chunks[0].padEnd(maxNombre) + tot + '\n';

  // Líneas siguientes solo nombre
  for (let i = 1; i < chunks.length; i++) {
    lines += ' '.repeat(4) + chunks[i] + '\n';
  }

  const modificaciones = normalizeModificaciones(item);
  if (modificaciones.length) {
    lines += EP.BOLD_ON + '   -> MODIFICACIONES:' + EP.BOLD_OFF + '\n';
    for (const mod of modificaciones) {
      const modLines = wrapText(mod.toUpperCase(), 40);
      lines += '      - ' + modLines[0] + '\n';
      for (let i = 1; i < modLines.length; i++) {
        lines += '        ' + modLines[i] + '\n';
      }
    }
  }

  lines += '\n';

  return lines;
}

// ─── Constructor ESC/POS ──────────────────────────────────────────────
function buildEscPos({
  numeroComanda, cliente, telefono, canal,
  horaRetiro, medioPago, pagado, observacion,
  pedido, subtotal, descuento, totalFinal, trabajador = false,
}) {
  const W   = 48;
  const SEP = '-'.repeat(W);
  const fecha = new Date().toLocaleString('es-CL', {
    dateStyle: 'short', timeStyle: 'short',
  });

  let t = EP.INIT + EP.CP858;

  // Encabezado centrado
  t += EP.CENTER;
  t += EP.BOLD_ON + 'La Sushitería' + '\n' + EP.BOLD_OFF;
  t += EP.BOLD_ON + EP.DBL + `Comanda #${numeroComanda}` + EP.NORMAL + EP.BOLD_OFF + '\n';
  t += fecha + '\n';

  // Datos del pedido
  t += EP.LEFT;
  t += SEP + '\n';
  t += EP.BOLD_ON + EP.DBL_H + `Cliente   ${(cliente || 'Sin nombre').toUpperCase()}` + EP.NORMAL + EP.BOLD_OFF + '\n';
  if (telefono) t += `Teléfono  ${telefono}` + '\n';
  t += `Canal     ${canal || '-'}` + '\n';
// Ya no mostramos retiro arriba
  if (trabajador) {
    t += `Tipo      TRABAJADOR` + '\n';
  } else {
    t += `Pago      ${medioPago || '-'}` + '\n';
    t += `Pagado    ${pagado ? 'Sí' : 'No'}` + '\n';
  }


  // Líneas de productos  (28 + 5 + 15 = 48)
  t += SEP + '\n';
  t += 'CNT PRODUCTO'.padEnd(33) + 'TOTAL'.padStart(15) + '\n';
  for (const it of (pedido || [])) {
    t += buildItemLines(it);
  }

  // Totales
  t += SEP + '\n';
  t += rowLR('Subtotal', fmtRaw(subtotal)) + '\n';
  if (Number(descuento) > 0) {
    t += rowLR('Descuento', '-' + fmtRaw(descuento)) + '\n';
  }

  // TOTAL en doble tamaño
const totalStr = fmtRaw(totalFinal);
const sp = Math.max(1, 24 - 5 - totalStr.length);

t += SEP + '\n';
if (trabajador) t += EP.BOLD_ON + 'NO VENTA - TRABAJADOR' + EP.BOLD_OFF + '\n';
t += EP.BOLD_ON + EP.DBL;
t += 'TOTAL' + ' '.repeat(sp) + totalStr + '\n';
t += EP.NORMAL + EP.BOLD_OFF;

// ─── HORA RETIRO DESTACADA ─────────────────────
t += '\n';
t += EP.CENTER;
t += EP.BOLD_ON + EP.DBL;
t += 'RETIRO\n';
t += (horaRetiro || 'NO DEFINIDA') + '\n';
t += EP.NORMAL + EP.BOLD_OFF;

// ─── OBSERVACIÓN DESTACADA ─────────────────────
if (observacion) {
  t += '\n';
  t += EP.LEFT;
  t += SEP + '\n';
  t += EP.BOLD_ON + EP.DBL_H;
  t += 'OBSERVACION\n';
  t += EP.NORMAL;
  t += EP.BOLD_ON;
  t += observacion.toUpperCase() + '\n';
  t += EP.BOLD_OFF;
}

  t += EP.FEED1;

  return t;
}

/** Abre la URL rawbt: sin interferir con React Router. */
function dispatchRawBT(url) {
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.style.cssText = 'display:none;position:absolute;';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.parentNode?.removeChild(a), 500);
}

// ─── Fallback HTML (ventana popup para desktop/dev) ───────────────────
function fmtCLP(n) {
  const num = Number(n) || 0;
  return '$ ' + num.toLocaleString('es-CL');
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function printHtmlPopup({
  numeroComanda, cliente, telefono, canal,
  horaRetiro, medioPago, pagado, observacion,
  pedido, subtotal, descuento, totalFinal, trabajador = false,
}) {
  const fecha = new Date().toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

 const itemsHTML = (pedido || [])
  .map((it) => {
    const modificaciones = normalizeModificaciones(it);
    const modsHTML = modificaciones.length
      ? `<tr class="item-mods-row"><td></td><td colspan="2">
          <div class="mods-title">↳ MODIFICACIONES:</div>
          <ul class="mods-list">
            ${modificaciones.map((m) => `<li>${escapeHtml(m).toUpperCase()}</li>`).join('')}
          </ul>
        </td></tr>`
      : '';
    return `
    <tr>
      <td class="col-cant">x${Number(it.cantidad) || 0}</td>
      <td class="col-nombre">${escapeHtml(it.nombre || '')}</td>
      <td class="col-precio">${fmtCLP(Number(it.precio) * Number(it.cantidad))}</td>
    </tr>${modsHTML}`;
  })
  .join('');

  const descuentoHTML =
    Number(descuento) > 0
      ? `<tr class="row-desc"><td colspan="2">Descuento</td><td>-${fmtCLP(descuento)}</td></tr>`
      : '';

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Comanda #${numeroComanda}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      width: 80mm;
      margin: 0 auto;
      padding: 4mm 4mm 8mm;
      color: #000;
    }
    .header { text-align: center; margin-bottom: 6px; }
    .header h1 { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
    .header p  { font-size: 11px; }
    .divider { border-top: 1px dashed #000; margin: 5px 0; }
    .datos { font-size: 11px; margin-bottom: 4px; }
    .datos tr td:first-child { font-weight: bold; padding-right: 6px; width: 40%; }
    table.items {
      width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 11px;
    }
    table.items thead tr { border-bottom: 1px solid #000; }
    table.items thead th {
      text-align: left; padding: 2px 0; font-size: 10px; text-transform: uppercase;
    }
    table.items tbody td { padding: 2px 0; vertical-align: top; }
    .col-nombre { width: 55%; word-break: break-word; }
    .col-cant   { width: 10%; text-align: center; }
    .col-precio { width: 35%; text-align: right; }
    .item-mods-row td { padding: 0 0 4px; }
    .mods-title { font-size: 10px; font-weight: 800; margin: 1px 0; }
    .mods-list { margin: 0 0 0 12px; padding: 0; font-size: 10px; font-weight: 700; text-transform: uppercase; }
    .mods-list li { margin: 0 0 1px; overflow-wrap: anywhere; word-break: break-word; }
    .comanda-numero {
      font-size: 22px;
      font-weight: 800;
      text-align: center;
      margin: 4px 0;
    }
    .cliente-nombre {
      font-size: 20px;
      font-weight: 800;
      text-align: center;
      text-transform: uppercase;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    table.totales {
      width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px;
    }
    table.totales td { padding: 1px 0; }
    table.totales td:last-child { text-align: right; }
    .row-desc td { font-size: 11px; }
    .total-final td {
      font-size: 15px; font-weight: bold;
      border-top: 1px solid #000; padding-top: 3px;
    }
    .footer { text-align: center; margin-top: 8px; font-size: 10px; }

    .retiro-box {
  margin-top: 10px;
  text-align: center;
  border-top: 2px dashed #000;
  border-bottom: 2px dashed #000;
  padding: 8px 0;
}

.retiro-title {
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 1px;
}

.retiro-hora {
  font-size: 42px;
  font-weight: bold;
  margin-top: 2px;
}

.obs-box {
  margin-top: 10px;
  border: 2px solid #000;
  padding: 8px;
}

.obs-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
}

.obs-text {
  font-size: 26px;
  font-weight: bold;
  word-break: break-word;
  text-transform: uppercase;
}
    @media print {
      @page { size: 80mm auto; margin: 0; }
      body { width: 80mm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>La Sushitería</h1>
    <p class="comanda-numero">COMANDA #${numeroComanda}</p>
    <p>${fecha}</p>
  </div>
  <div class="divider"></div>
  <table class="datos">
    <tr><td>Cliente</td><td class="cliente-nombre">${cliente || 'Sin nombre'}</td></tr>
    ${telefono ? `<tr><td>Teléfono</td><td>${telefono}</td></tr>` : ''}
    <tr><td>Canal</td><td>${canal || '—'}</td></tr>
    ${trabajador
      ? '<tr><td>Tipo</td><td>TRABAJADOR</td></tr>'
      : `<tr><td>Pago</td><td>${medioPago || '—'}</td></tr>
    <tr><td>Pagado</td><td>${pagado ? 'Sí' : 'No'}</td></tr>`}
    
  </table>
  <div class="divider"></div>
  <table class="items">
    <thead>
      <tr>
        <th class="col-cant">Cnt</th>
        <th class="col-nombre">Producto</th>
        <th class="col-precio">Total</th>
      </tr>
    </thead>
    <tbody>${itemsHTML}</tbody>
  </table>
  <div class="divider"></div>
<table class="totales">
  <tr><td>Subtotal</td><td>${fmtCLP(subtotal)}</td></tr>
  ${descuentoHTML}
  ${trabajador ? '<tr class="row-desc"><td colspan="2">NO VENTA - TRABAJADOR</td><td></td></tr>' : ''}
  <tr class="total-final"><td>TOTAL</td><td>${fmtCLP(totalFinal)}</td></tr>
</table>

<div class="retiro-box">
  <div class="retiro-title">RETIRO</div>
  <div class="retiro-hora">
    ${horaRetiro || 'NO DEFINIDA'}
  </div>
</div>

${observacion ? `
<div class="obs-box">
  <div class="obs-title">OBSERVACIÓN</div>
  <div class="obs-text">
    ${observacion}
  </div>
</div>
` : ''}
  <script>
    window.onload = function () {
      window.print();
      window.onafterprint = function () { window.close(); };
      setTimeout(function () { window.close(); }, 3000);
    };
  </script>
</body>
</html>`;

  const popup = window.open('', '_blank', 'width=340,height=600,scrollbars=yes');
  if (!popup) {
    alert('Permití las ventanas emergentes para imprimir la comanda.');
    return;
  }
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
}

// ─── Exported function ────────────────────────────────────────────────
export function imprimirComanda(data) {
  if (shouldUseRawBT()) {
    const escpos = cp858(thermalSafeText(buildEscPos(data)));
    dispatchRawBT('rawbt:' + rawbtEncode(escpos));
  } else {
    printHtmlPopup(data);
  }
}
